<script setup lang="ts">
// 品牌出场仪式 — 旋转体验与 admin-tsx 登录页 UmbrellaLogo 对齐:
// 伞面 2s/圈顺时针永不停转 (force 模式, 系统关动画也照转, 无外环圆圈)
// 盾牌登场: 金色盾牌一次性放大登场后保持静止, 伞面在盾牌后继续转动
// 组件自包含计时器, 头部/底部/登录页引入即自动播放
import { ref, onMounted, onUnmounted } from 'vue'
import { site } from '@/i18n'
import UmbLogo from '@/components/UmbLogo.vue'

const props = withDefaults(defineProps<{
  size?: number
  /** 盾牌登场延时(ms), 之前为裸伞旋转阶段; 伞面全程永不停转 */
  spinDuration?: number
}>(), {
  size: 64,
  spinDuration: 3400
})

// 多实例同页 (头部+底部+登录页) 时避免 SVG 渐变 id 冲突
const uid = Math.random().toString(36).slice(2, 8)

const phase = ref<'spin' | 'shield'>('spin')
let timer: number | null = null

onMounted(() => {
  timer = window.setTimeout(() => { phase.value = 'shield' }, props.spinDuration)
})

onUnmounted(() => {
  if (timer) clearTimeout(timer)
})
</script>

<template>
  <span
    class="umb-ritual"
    :class="phase"
    :style="{ width: `${size}px`, height: `${size}px` }"
    role="img"
    :aria-label="`${site.brand.name} · ${site.seo.brandMark}`"
  >
    <!-- 保护伞本体: 伞面持续顺时针旋转 (admin-tsx 同款 2s/圈 force 永转), 盾牌登场后也不停止 -->
    <UmbLogo class="ritual-logo" :badge="false" spin/>

    <!-- 阶段二: 金盾在伞前登场
         type=animation: 让 Vue 按 animationend 时机移除 enter 类, 避免动画被掐断 -->
    <Transition :name="phase === 'shield' ? 'shield-pop' : ''" type="animation" appear>
      <div v-if="phase === 'shield'" class="shield-box" aria-hidden="true">
        <svg class="shield-svg" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient :id="`umbGold-${uid}`" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#ffe48c"/>
              <stop offset="45%" stop-color="#d4af37"/>
              <stop offset="100%" stop-color="#8b6914"/>
            </linearGradient>
            <linearGradient :id="`umbShine-${uid}`" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="#ffffff" stop-opacity="0.7"/>
              <stop offset="50%" stop-color="#ffffff" stop-opacity="0"/>
            </linearGradient>
          </defs>
          <path d="M32 3 L56 12 V29 C56 44.5 46 55.5 32 61 C18 55.5 8 44.5 8 29 V12 Z" :fill="`url(#umbGold-${uid})`" stroke="#6b5013" stroke-width="2" stroke-linejoin="round"/>
          <path d="M32 9 L50 16 V29 C50 41 42.5 50 32 55 C21.5 50 14 41 14 29 V16 Z" fill="none" stroke="#fff5cc" stroke-width="1.5" opacity="0.8"/>
          <path d="M20 18 L30 26 M44 18 L34 26" fill="none" :stroke="`url(#umbShine-${uid})`" stroke-width="2" stroke-linecap="round" opacity="0.7"/>
        </svg>
        <!-- 星光 -->
        <span class="spark spark-1">✦</span>
        <span class="spark spark-2">✦</span>
        <span class="spark spark-3">✧</span>
      </div>
    </Transition>
  </span>
</template>

<style scoped>
.umb-ritual {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ritual-logo {
  position: absolute;
  /* 品牌伞主体: 充满整个容器, 不被盾牌遮挡视觉主体 */
  width: 100%;
  height: 100%;
}

.ritual-logo :deep(.umb-logo) {
  width: 100%;
  height: 100%;
  display: block;
  filter: drop-shadow(0 1px 4px rgba(13, 16, 23, 0.18));
}

/* ── 伞面旋转由 UmbLogo 的 spin prop 承担 (force 永转, 无外环) ── */

.shield-box {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: 50% 50%;
}

.shield-svg {
  /* 盾牌点缀尺寸: 居中浮在伞前, 伞瓣四周完整可见 */
  width: 50%;
  height: 50%;
  filter: drop-shadow(0 2px 8px rgba(212, 175, 55, 0.45));
  transform-origin: 50% 50%;
}

/* 盾牌登场后保持静止: 不挂任何循环动画 (尺度呼吸会显廉价), 仅保留静态投影 */

/* 盾牌放大登场: 直接挂在 Transition 贴类的 .shield-box 本身 (同元素双类, 无空格) */
.shield-pop-enter-active.shield-box {
  animation: umb-shield-entrance 1400ms cubic-bezier(0.16, 1, 0.3, 1) both !important;
}

@keyframes umb-shield-entrance {
  0%   { opacity: 0; transform: scale(0.38) rotate(-22deg); }
  52%  { opacity: 1; transform: scale(1.055) rotate(3deg); }
  70%  { opacity: 1; transform: scale(0.985) rotate(-1.5deg); }
  85%  { opacity: 1; transform: scale(1.018) rotate(0.6deg); }
  100% { opacity: 1; transform: scale(1)     rotate(0deg); }
}

.spark {
  position: absolute;
  color: #d4af37;
  line-height: 1;
  pointer-events: none;
  text-shadow: 0 0 10px rgba(255, 228, 140, 0.9);
}
.spark-1 { top: 6%;   right: 4%;   font-size: 13px; }
.spark-2 { bottom: 4%; left: 0;     font-size: 10px; }
.spark-3 { top: 24%;  right: -6%;  font-size: 8px; }

@media (prefers-reduced-motion: no-preference) {
  .spark { animation: umb-spark-twinkle 2s ease-in-out infinite; opacity: 0; }
  .spark-1 { animation-delay: 0.2s; }
  .spark-2 { animation-delay: 0.8s; }
  .spark-3 { animation-delay: 1.3s; }
}

@keyframes umb-spark-twinkle {
  0%, 100% { opacity: 0; transform: scale(0.3) rotate(0deg); }
  50%      { opacity: 1; transform: scale(1) rotate(180deg); }
}
</style>
